import React, { useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router'
import { useAtomValue, useSetAtom } from 'jotai'

import {
	PageTitle,
	RevenueAgencyCreateModal,
	RevenueAgencyListTable,
	RevenueAgencyRemoveModal,
	RevenueAgencyEditModal,
	RevenueAgencyEditNotesModal,
	RevenueAgencyHistoryListModal,
	UiPagination,
	ToolPanel,
	IconPlus,
	ESortOrder
} from '../components'
import { Filters, FiltersButtonIcon } from '../components/filters'
import { useRevenueAgencyList, useRevenueAgencyFiltersPage, useRevenueAgencySortPage } from '../hooks'
import * as lib from '../lib'
import * as state from '../states'
import { IFetchItemsSetup, IRevenueAgency, ERoleAccess } from '../types'
import { TAKE, MENU, MENU_CATEGORY } from '../constants'
import { Access } from '../lib'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { ALL_LANG_KEY } from '../types/lang'

const TopButtonsBox = styled.div`
    display: flex;
    flex-direction: row;
    gap: 15px;
`

export const RevenueAgencyPage = (): React.JSX.Element => {
	const { t } = useTranslation()
	const setFetchIsCommonToReload = useSetAtom(state.fetchIsCommonToReloadAtom)
	const tokens = useAtomValue(state.tokensAtom)
	const access = tokens?.access ?? null
	const setFetchIsLoading = useSetAtom(state.fetchIsLoadingAtom)

	const profile = useAtomValue(state.profileAtom)
	const $access = new Access(profile ?? null)

	const location = useLocation()
	const href = lib.pageHref(location, TAKE)
	const navigate = useNavigate()
	const [searchParams, setSearchParams] = useSearchParams()

	const page = lib.skipSearchParams(searchParams.get('page')) ?? 0
	const take = lib.takeSearchParams(searchParams.get('take')) ?? TAKE
	const skip = (take === 'all') ? 0 : page > 0 ? page * take : 0

	const pageTitle = `${t(MENU_CATEGORY.revenue.title)} ${t(MENU.revenueAgency.title)}`

	const {
		filter,
		send,
		activeFiltersCount,
		filterFillFactor,
		isFiltersPanelOpen,
		setIsFiltersPanelOpen,
		revenueAgencyFiltersConfig,
		defaultFiltersValue,
		initialFiltersValue,
		clearChildFilters,
		setUserFilters
	} = useRevenueAgencyFiltersPage()

	//TODO: check sort output var type ?
	const {
		// sort: sortTmp,
		sortBy,
		setSortBy,
		order,
		setOrder
	} = useRevenueAgencySortPage()

	const setup: IFetchItemsSetup = {
		filter,
		sort: order !== ESortOrder.unset ? [[sortBy, order]] : [],
		skip,
		take
	}
	const { error, items, total, warmUp } = useRevenueAgencyList(setup, send)

	const [createRevenueAgency, setCreateRevenueAgency] = useState<boolean>(false)
	const [editRevenueAgency, setEditRevenueAgency] = useState<IRevenueAgency | null>(null)
	const [editNotesRevenueAgency, setEditNotesRevenueAgency] = useState<IRevenueAgency | null>(null)
	const [removeRevenueAgency, setRemoveRevenueAgency] = useState<IRevenueAgency | null>(null)
	const [viewRevenueAgencyHistory, setViewRevenueAgencyHistory] = useState<IRevenueAgency | null>(null)

	const onCreate = (e: React.MouseEvent) => {
		e.preventDefault()
		setCreateRevenueAgency(true)
	}
	const onEdit = (item: IRevenueAgency, e: React.MouseEvent) => {
		e.preventDefault()
		setEditRevenueAgency(item)
	}
	const onEditNotes = (item: IRevenueAgency, e: React.MouseEvent) => {
		e.preventDefault()
		setEditNotesRevenueAgency(item)
	}
	const onRemove = (item: IRevenueAgency, e: React.MouseEvent) => {
		e.preventDefault()
		setRemoveRevenueAgency(item)
	}
	const onViewRevenueAgencyHistory = (item: IRevenueAgency, e: React.MouseEvent) => {
		e.preventDefault()
		setViewRevenueAgencyHistory(item)
	}

	return (
		<>
			<PageTitle title={pageTitle}/>

			<div className="card rounded-3 border-0 mb-24">
				<div className="card-body p-25">
					{/* remove CREATE button for 'account' main page (without pre-selected group) */}
					<div className="d-flex align-items-center justify-content-between pb-20 border-bottom border-color">
						<FiltersButtonIcon
							activeFiltersCount={activeFiltersCount}
							filterFillFactor={filterFillFactor}
							onClick={() => setIsFiltersPanelOpen((val) => !val)}
						/>

						<TopButtonsBox>
							{
								!!onCreate && !!$access.$(ERoleAccess.RevenueAgencyCreate)
									? (
										<button
											type="button"
											className="btn btn-primary d-flex align-items-center"
											onClick={onCreate}
										>
											<IconPlus/>
											<span className="d-none d-lg-block">{`${t(ALL_LANG_KEY.revenue_agency_page.btn_add_new)}`}</span>
										</button>
									)
									: null
							}
						</TopButtonsBox>

					</div>

					<Filters
						config={revenueAgencyFiltersConfig}
						initialFiltersValue={initialFiltersValue}
						resetFiltersValue={defaultFiltersValue}
						isAutoModeWithDebounce={false}
						isFiltersPanelOpen={isFiltersPanelOpen}
						isFiltersToClear={clearChildFilters}
						setFiltersCB={(value) => {
							//console.log('page.RevenueAgencyPage::setFiltersCB::', { value })
							setUserFilters(value)
						}}
					/>

					<RevenueAgencyListTable
						items={items}
						skip={skip}
						sortBy={sortBy}
						setSortBy={setSortBy}
						order={order}
						setOrder={setOrder}
						onEdit={onEdit}
						onEditNotes={onEditNotes}
						onRemove={onRemove}
						onViewHistory={onViewRevenueAgencyHistory}
					/>

					<ToolPanel flow="<-">
						<UiPagination
							classesToAdd={{
								wrapper: 'mt-25'
							}}
							page={page}
							take={take}
							total={total}
							href={(page: number) => href(page).tail}
						/>
					</ToolPanel>
				</div>
			</div>

			{
				!!createRevenueAgency && !!$access.$(ERoleAccess.RevenueAgencyCreate) && <RevenueAgencyCreateModal
					show={createRevenueAgency}
					onClose={() => setCreateRevenueAgency(false)}
					onSave={() => {
						setCreateRevenueAgency(false)
						navigate(href(0).tail)
						warmUp()
						setFetchIsCommonToReload(state.launchUpdateListRevenueAgency)
					}}
				/>
			}

			{
				!!editRevenueAgency && !!$access.$(ERoleAccess.RevenueAgencyModify) && <RevenueAgencyEditModal
					item={editRevenueAgency}
					show={!!editRevenueAgency}
					onClose={() => setEditRevenueAgency(null)}
					onSave={() => {
						setEditRevenueAgency(null)
						navigate(href(0).tail)
						warmUp()
						setFetchIsCommonToReload(state.launchUpdateListRevenueAgency)
					}}
				/>
			}

			{
				!!editNotesRevenueAgency && !!$access.$(ERoleAccess.RevenueAgencyList) && <RevenueAgencyEditNotesModal
					isViewMode={!$access.$(ERoleAccess.RevenueAgencyModify)}
					item={editNotesRevenueAgency}
					show={!!editNotesRevenueAgency}
					onClose={() => setEditNotesRevenueAgency(null)}
					onSave={() => {
						setEditNotesRevenueAgency(null)
						//navigate(href(0).tail)
						warmUp()
					}}
				/>
			}

			{
				!!removeRevenueAgency && !!$access.$(ERoleAccess.RevenueAgencyRemove) && <RevenueAgencyRemoveModal
					item={removeRevenueAgency}
					show={!!removeRevenueAgency}
					onClose={() => setRemoveRevenueAgency(null)}
					onSave={() => {
						setRemoveRevenueAgency(null)
						navigate(href(0).tail)
						warmUp()
						setFetchIsCommonToReload(state.launchUpdateListRevenueAgency)
					}}
				/>
			}

			{
				!!viewRevenueAgencyHistory && !!$access.$(ERoleAccess.RevenueAgencyList) && <RevenueAgencyHistoryListModal
					revenueAgency={viewRevenueAgencyHistory}
					show={!!viewRevenueAgencyHistory}
					take={take}
					onClose={() => {
						setViewRevenueAgencyHistory(null)
						// navigate(href(0).tail)
						warmUp()
					}}
				/>
			}
		</>
	)
}
